import { useEffect, useState } from "react";

/**
 * Progresso simulado compartilhado entre todos os loaders visíveis.
 * As APIs do BI não enviam percentual real; a curva aproxima o tempo
 * típico das consultas ao Sienge e trava perto de 96% até o fetch acabar.
 */
const CAP = 96;
const TAU_MS = 6800;
const TICK_MS = 80;
const RESET_GRACE_MS = 80;

let subscriberCount = 0;
let startedAt = 0;
let intervalId: number | null = null;
let resetTimer: number | null = null;
const listeners = new Set<(percent: number) => void>();

function computePercent(origin: number): number {
  const elapsed = performance.now() - origin;
  const next = CAP * (1 - Math.exp(-elapsed / TAU_MS));
  return Math.max(2, Math.min(CAP, Math.round(next)));
}

function broadcast(): void {
  const value = computePercent(startedAt);
  listeners.forEach((listener) => listener(value));
}

function startTicking(): void {
  if (intervalId != null) return;
  startedAt = performance.now();
  broadcast();
  intervalId = window.setInterval(broadcast, TICK_MS);
}

function stopTicking(): void {
  if (intervalId != null) {
    window.clearInterval(intervalId);
    intervalId = null;
  }
  startedAt = 0;
}

function subscribe(listener: (percent: number) => void): () => void {
  if (resetTimer != null) {
    window.clearTimeout(resetTimer);
    resetTimer = null;
  }

  subscriberCount += 1;
  listeners.add(listener);

  if (subscriberCount === 1 || startedAt === 0) {
    startTicking();
  } else {
    listener(computePercent(startedAt));
  }

  return () => {
    listeners.delete(listener);
    subscriberCount = Math.max(0, subscriberCount - 1);
    if (subscriberCount > 0) return;

    resetTimer = window.setTimeout(() => {
      resetTimer = null;
      if (subscriberCount === 0) stopTicking();
    }, RESET_GRACE_MS);
  };
}

export function useSimulatedProgress(active = true): number {
  const [percent, setPercent] = useState(active ? 2 : 0);

  useEffect(() => {
    if (!active) {
      setPercent(0);
      return;
    }
    return subscribe(setPercent);
  }, [active]);

  return percent;
}
